import { Navigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { AppRoutes } from "src/common/constants/constantEnums";
import { GlobalUtils } from "src/utils/globalUtils";

interface ProtectedRouteProps {
  element: JSX.Element;
}

export const ProtectedRoute = ({ element }: ProtectedRouteProps) => {
  const token = localStorage.getItem("authToken");
  const { approved, applicationStatus } = GlobalUtils.getAuthStateFromToken(token);
  const pendingAccess = (localStorage.getItem("pendingAccess") || "").toLowerCase() === "true";
  const location = useLocation();

  const clearSession = () => {
    try {
      localStorage.removeItem("authToken");
      localStorage.removeItem("userRole");
      localStorage.removeItem("accountInfo");
      localStorage.removeItem("cachedSubmitDetails");
      localStorage.removeItem("pendingAccess");
    } catch {}
  };

  const path = location.pathname;
  const isSubmit = path === AppRoutes.SUBMIT;
  const isRegistrationForm = path === AppRoutes.REGISTRATION_FORM;
  const isRegister = path === "/register";

  // If no token, force login for all protected routes
  if (!token) {
    return <Navigate to={AppRoutes.LOGIN} replace state={{ from: location }} />;
  }

  // Approved users
  // Approved users should NOT access: /register, /registration-form, /submit
  if (approved && (isRegister || isRegistrationForm || isSubmit)) {
    clearSession();
    return <Navigate to={AppRoutes.LOGIN} replace />;
  }

  // If approved and not in the blocked list -> allow
  if (approved) {
    return element;
  }

  // Unapproved + applicationStatus === Completed (from JWT claims)
  if (applicationStatus === "Completed" && !isSubmit) {
    clearSession();
    return <Navigate to={AppRoutes.LOGIN} replace />;
  }
  if (applicationStatus === "Completed" && isSubmit) {
    return element;
  }

  // New registration entry (/register)
  // Clear any stale draft data but keep token/session so the flow can continue
  if (isRegister) {
    localStorage.removeItem("applicationInfo");
    return element;
  }

  // Draft or in-progress registration (/registration-form)
  if (isRegistrationForm) {
    const appInfoRaw = localStorage.getItem("applicationInfo");
    const appInfo = appInfoRaw ? JSON.parse(appInfoRaw) : null;
    const hasDraft = appInfo && applicationStatus === "In_Progress";

    // Allow if:
    // - user has a draft + token (resume case), OR
    // - user just registered (no draft yet, but has token)
    if ((hasDraft && token) || (!appInfo && token)) {
      return element;
    }

    // Otherwise, send to login
    clearSession();
    return <Navigate to={AppRoutes.LOGIN} replace />;
  }

  // Pending users: Submit page access
  // Only allow /submit when pendingAccess is set (immediately after submit/login)
  if (isSubmit) {
    return pendingAccess && token ? element : <Navigate to={AppRoutes.LOGIN} replace />;
  }

  // Fallback for any other route when NOT approved
  clearSession();
  return <Navigate to={AppRoutes.LOGIN} replace />;
};
